import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { useEffect } from "react"
import { apiClientV2 } from "../../../services/api/apiClient"
import type { PageGeometry } from "./declarationTemplateApi"
import { parseContentDispositionFilename } from "./inquiryDraftApi"

const BASE = "/material-compliance/conformity"

export type ConformityStatus = "draft" | "approved" | "downloaded" | "sent"
// Filter-only virtual value: "issued" collapses downloaded + sent for the
// inbox filter chip and the merged counter card. Row-level `status` never
// takes this value — it stays one of the four real states above.
export type ConformityStatusFilter = ConformityStatus | "issued"
// v1 rows: customer | product | article. v2 rows (one document per scope)
// additionally use "products" and "general" (TUR-1573: no product list at all).
export type ConformityScope =
  | "customer"
  | "product"
  | "article"
  | "products"
  | "general"

/** Discriminated body for /preview and /generate — pick the scope, provide the
 *  matching id list. The backend rejects mismatches at the edge. */
export type ConformityTargetBody =
  | { scope: "customer"; customerIds: string[]; framework: string }
  | { scope: "product"; productIds: string[]; framework: string }
  | { scope: "article"; articleIds: string[]; framework: string }

// ─── Wire shapes (apiClientV2 converts snake_case → camelCase) ────────────────

export interface ConformitySubstanceLine {
  name: string | null
  casNumber: string | null
  concentrationPct: number | null
}

export interface ConformityArticle {
  articleId: string
  articleNumber: string | null
  articleName: string | null
  /** From mc_articles.material_group — shown right-aligned on the article row. */
  materialGroup: string | null
  hasSvhcHit: boolean
  /** True when the framework verdict for this article is missing. Preview
   *  surfaces this so the FE can flag the article and disable Generate; the
   *  /generate endpoint also hard-blocks. */
  hasGap: boolean
  statement: string | null
  citationRef: string | null
  substances: ConformitySubstanceLine[]
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** POST /preview — build draft previews in-memory (no persistence). Mutation form. */
// ─── Helpers ──────────────────────────────────────────────────────────────────

/** The id list the scope selects. Exported for the agent chat, which needs the
 *  subject count for its confirmation. */
// ─── List + detail + approve ──────────────────────────────────────────────────

export interface ConformityListItem {
  id: string
  reference: string | null
  status: ConformityStatus
  needsReview: boolean
  // True when any linked article has unresolved gap questions for this
  // framework. Renders the "Incomplete data" pill and the PDF skips the
  // clean/contains statement.
  hasGap: boolean
  scope: ConformityScope
  customerId: string | null
  customerName: string | null
  productId: string | null
  productNumber: string | null
  productName: string | null
  articleId: string | null
  articleNumber: string | null
  articleName: string | null
  /** v1 rows carry their single framework; v2 rows carry null here and their
   *  per-framework children in `frameworkKeys` / detail `frameworks`. */
  framework: string | null
  /** `mc_framework_codes.normalize_framework_code(framework)` — a single code
   *  (one framework per v1 conformity row, unlike the list[str] siblings on the
   *  other list items). null for v2 rows and unmapped values. */
  frameworkCode: string | null
  // ── v2 columns ──
  isV2: boolean
  /** Selected + not blocked framework keys, in FRAMEWORK_ORDER. */
  frameworkKeys: string[]
  revision: number | null
  issuedAt: string | null
  validUntil: string | null
  thresholdPct: number | null
  language: string | null
  articleCount: number
  generatedAt: string | null
  approvedAt: string | null
  approvedBy: string | null
  downloadedAt: string | null
  downloadedBy: string | null
  /** Applied organization_signatures.id, or null when unsigned. Behind
   *  ENABLE_ORG_SIGNATURES — see `useUpdateConformitySignature`. */
  signatureId: string | null
  /** General declarations only (TUR-1573): the product family this letter
   *  covers. null for customer/products scope rows. */
  productDescription: string | null
}

export interface ConformityListPage {
  items: ConformityListItem[]
  total: number
  page: number
  pageSize: number
}

export interface ConformityCounts {
  needsReview: number
  ready: number
  approved: number
  downloaded: number
  sent: number
}

/** GET /conformity/counts — four-bucket totals for the inbox StatGrid.
 *  Cheap aggregate query; refetched on window focus so counts reflect
 *  approve/download activity from other tabs. */
export function useConformityCounts() {
  return useQuery({
    queryKey: ["mc", "conformity", "counts"],
    queryFn: () =>
      apiClientV2.get<ConformityCounts>(`${BASE}/counts`).then((r) => r.data),
    staleTime: 30 * 1000,
  })
}

/** The states this build knows how to render. The wire type is widened; this
 *  is the closed set, and `isComputing` is the only bridge between them. */
export type ConformityEvaluationState = "ready" | "computing"

/** True only for the state that should block Approve and drive polling.
 *  Anything else, including a state this build has never seen and the field
 *  being absent entirely, reads as "not computing": an unknown state must
 *  degrade to the usable UI, never to a permanently disabled one. */
export function isComputing(state: string | undefined): boolean {
  return state === "computing"
}

export interface ConformityDetail extends ConformityListItem {
  snapshotHash: string | null
  /** Primary contact email (mc_customer_contacts.is_primary=true) or the
   *  customer row's own email — resolved server-side, never fabricated. */
  recipientEmail: string | null
  /** Per-declaration cc-list — the "+ Add" chips on the editor. */
  extraRecipientEmails: string[]
  /** Tenant organisation name — used as the "ISSUED BY" heading. */
  issuerName: string | null
  /** Cover-note copy. Auto-drafted on approve; user-editable via PATCH. */
  coverSubject: string | null
  coverBody: string | null
  /** Send trail — populated after the first successful send. */
  sentAt: string | null
  sentBy: string | null
  emailMessageId: string | null
  sendCount: number
  articles: ConformityArticle[]
  /** v2 rows only — the persisted evaluation children (null on v1 rows). */
  frameworks: ConformityV2Framework[] | null
  subjects: ConformityV2Subject[] | null
  /** Customer scope prints its full product table in Annex 1 instead of naming
   *  the scope by reference. Per declaration, draft only (WP3). */
  printProductList: boolean
  /** WP2: "computing" while any product's precomputed facts are behind the
   *  master data. Derived server-side per request, never stored, so it can
   *  never claim "ready" about a stale answer. Always "ready" for orgs that do
   *  not read facts.
   *
   *  OPTIONAL AND WIDENED ON PURPOSE. Frontend and backend deploy and roll back
   *  independently, so this frontend may be talking to a backend that predates
   *  the field (absent) or postdates it (a state this build has never heard of).
   *  Read it through `isComputing()`, never by comparing the raw string. */
  evaluationState?: ConformityEvaluationState | (string & {})
  /** How many of this declaration's products are still behind. 0 when ready,
   *  absent on a backend that predates the field. */
  pendingProductCount?: number
  /** Phase-4 template switcher (design §9): the pinned template's identity —
   *  null/null/false only when the pinned version itself has vanished, which
   *  should not normally happen since versions are immutable.
   *
   *  OPTIONAL, SAME REASONING AS `evaluationState` ABOVE: frontend and
   *  backend deploy independently, so a backend that predates this field
   *  leaves it absent rather than this frontend fabricating a value. */
  templateKey?: string | null
  templateName?: string | null
  templateIsDefault?: boolean
}

/** GET /conformity — paginated inbox listing. Every filter is optional and
 *  ANDs server-side. Free-text `search` matches ref + customer/product/
 *  article names + framework. Response carries `total` so the FE can render
 *  "Page N of M" without a second request. */
export interface ConformityListParams {
  ids?: string[]
  status?: ConformityStatusFilter
  // Restrict to the SVHC-flagged subset of drafts (server-side — see Fix B).
  // Typically combined with status="draft".
  needsReview?: boolean
  // Restrict to rows completed in the current calendar quarter (server-side).
  // Typically combined with status="issued".
  issuedPeriod?: "current_quarter"
  scope?: ConformityScope
  customerId?: string
  productId?: string
  framework?: string
  search?: string
  page?: number
  pageSize?: number
}

export function useConformityList(params: ConformityListParams = {}) {
  return useQuery({
    queryKey: ["mc", "conformity", "list", params],
    queryFn: () =>
      apiClientV2
        .get<ConformityListPage>(BASE, {
          params: {
            ids: params.ids,
            status: params.status,
            needs_review: params.needsReview,
            issued_period: params.issuedPeriod,
            scope: params.scope,
            customer_id: params.customerId,
            product_id: params.productId,
            framework: params.framework,
            search: params.search,
            page: params.page,
            page_size: params.pageSize,
          },
          // axios v1 defaults to bracket notation (`?ids[]=a&ids[]=b`) for
          // arrays; FastAPI's `Query(list[str])` reads only the repeated-key
          // form (`?ids=a&ids=b`), so force that here.
          paramsSerializer: { indexes: null },
        })
        .then((r) => r.data),
  })
}

/** How often to re-ask while a draft is still computing. Matches the queue's
 *  own latency target (< 60s) closely enough to feel immediate without turning
 *  an open editor into a request generator. */
const COMPUTING_POLL_MS = 2000

export function useConformityDetail(id: string | null) {
  return useQuery({
    queryKey: ["mc", "conformity", "detail", id],
    queryFn: () => fetchConformityDetail(id!),
    enabled: !!id,
    /** WP2: poll only while the server says something is still computing, and
     *  stop the moment it does not. Driven by the response rather than by a
     *  timer the FE owns, so a recompute that finishes early stops the polling
     *  early and one that never finishes cannot strand the UI in a fake
     *  loading state. */
    refetchInterval: (query) =>
      query.state.data?.evaluationState === "computing"
        ? COMPUTING_POLL_MS
        : false,
  })
}

interface ConformityApproveOut {
  id: string
  status: ConformityStatus
  approvedAt: string | null
  /** v2: allocated at approve — toast from THIS, the detail prop is stale. */
  reference: string | null
}

/** POST /conformity/{id}/approve — DRAFT → APPROVED. */
export function useApproveConformity() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2
        .post<ConformityApproveOut>(`${BASE}/${id}/approve`)
        .then((r) => r.data),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["mc", "conformity", "list"] })
      qc.invalidateQueries({ queryKey: ["mc", "conformity", "detail", id] })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

export interface ConformityRecipientsOut {
  id: string
  extraRecipientEmails: string[]
}

/** PATCH /conformity/{id}/recipients — replace the per-declaration cc-list.
 *  The primary recipient stays on mc_customer_contacts; this only manages
 *  the "+ Add" chips shown in the editor. */
export function useUpdateConformityRecipients() {
  const qc = useQueryClient()
  const mutation = useMutation({
    // apiClientV2 auto-converts camelCase → snake_case on the wire, matching the
    // backend's ConformityRecipientsUpdateIn field name.
    mutationFn: ({ id, emails }: { id: string; emails: string[] }) =>
      updateConformityRecipientsRequest(id, emails),
    // The send modal reads the recipients from the detail query and disables
    // Send while this mutation is pending. So the cache must hold the new
    // list before the mutation settles: write the saved list into the detail
    // straight away and wait for the refetch, otherwise a removed address
    // could still be sent to in the gap between PATCH and refetch.
    onSuccess: async (data, { id, emails }) => {
      const saved = data?.extraRecipientEmails ?? emails
      qc.setQueryData<ConformityDetail>(
        ["mc", "conformity", "detail", id],
        (old) => (old ? { ...old, extraRecipientEmails: saved } : old),
      )
      await qc.invalidateQueries({
        queryKey: ["mc", "conformity", "detail", id],
      })
      qc.invalidateQueries({
        queryKey: ["mc", "conformity", "preview-html", id],
      })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** PATCH /conformity/{id}/cover-note — edit subject/body between approve and
 *  send. Omit a field to leave it untouched; empty string clears it. Locked
 *  once the declaration has been sent or downloaded. */
export function useUpdateCoverNote() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      coverSubject,
      coverBody,
    }: {
      id: string
      coverSubject?: string
      coverBody?: string
    }) => updateCoverNoteRequest(id, { coverSubject, coverBody }),
    onSuccess: (_data, { id }) => invalidateConformityDeclaration(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** PATCH /conformity/{id}/signature — apply or clear the org signature applied
 *  to this declaration. `signatureId: null` clears it — signing is opt-in, so
 *  clicking the already-applied signature un-signs rather than falling back
 *  to any default. NOT locked once sent/downloaded (unlike /cover-note) —
 *  signature is a field this feature added to the existing flow, not a new
 *  edit restriction. Behind ENABLE_ORG_SIGNATURES at the router. */
export function useUpdateConformitySignature() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      signatureId,
    }: {
      id: string
      signatureId: string | null
    }) => updateConformitySignatureRequest(id, signatureId),
    onSuccess: (_data, { id }) => invalidateConformityDeclaration(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** PATCH /conformity/{id}/template — re-pin a draft to a different published
 *  template's current version (design §9). Draft only; the backend 409s
 *  TEMPLATE_NOT_PUBLISHED for a template that hasn't published. Invalidates
 *  the same trio `useUpdateConformitySignature` does: phase-1 text edits are
 *  slot-keyed and survive the re-pin, so refetching blocks is what surfaces
 *  the new layout rather than clobbering anything the user typed. */
export function useUpdateConformityTemplate() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ id, templateKey }: { id: string; templateKey: string }) =>
      updateConformityTemplateRequest(id, templateKey),
    onSuccess: (_data, { id }) => invalidateConformityDeclaration(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

interface ConformitySendOut {
  id: string
  status: ConformityStatus
  sentAt: string | null
  sentBy: string | null
  emailMessageId: string | null
  sendCount: number
}

/** POST /conformity/{id}/send — SES-dispatch the signed PDF with the cover note.
 *  The send modal supplies `to` (primary + extras merged) and optional last-
 *  minute cover copy overrides that are persisted to the row before dispatch. */
export function useSendConformity() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      to,
      cc,
      coverSubject,
      coverBody,
    }: {
      id: string
      to: string[]
      cc?: string[]
      coverSubject?: string
      coverBody?: string
    }) =>
      apiClientV2
        .post<ConformitySendOut>(`${BASE}/${id}/send`, {
          to,
          cc: cc ?? [],
          coverSubject,
          coverBody,
        })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["mc", "conformity", "list"] })
      qc.invalidateQueries({ queryKey: ["mc", "conformity", "counts"] })
      qc.invalidateQueries({ queryKey: ["mc", "conformity", "detail", id] })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** POST /{id}/send-test — mails the declaration to the CALLER only.
 *
 *  Carries the cover text being edited so the rehearsal shows the mail as it
 *  WOULD go out — but no recipient, on purpose: a recipient here is a recipient a
 *  caller could get wrong, and the whole point is that a pilot user can rehearse a
 *  send without a real customer receiving anything. The server writes nothing, so
 *  no cache invalidation either. */
export function useSendConformityTest() {
  const mutation = useMutation({
    mutationFn: ({
      id,
      coverSubject,
      coverBody,
    }: {
      id: string
      coverSubject?: string
      coverBody?: string
    }) =>
      apiClientV2
        .post<{ sentTo: string }>(`${BASE}/${id}/send-test`, {
          coverSubject,
          coverBody,
        })
        .then((r) => r.data),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** PATCH /{id}/language — the DRAFT's document language.
 *
 *  The editor's DE/EN buttons used to re-render the preview and nothing else, so a
 *  reviewer could read a German preview and issue an English document. Persisting it
 *  also decides which text_overrides apply — the renderer only applies edits written
 *  in the declaration's own language. */
export function useSetConformityLanguage() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ id, language }: { id: string; language: "de" | "en" }) =>
      apiClientV2
        .patch<ConformityDetail>(`${BASE}/${id}/language`, { language })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

// ─── Shared requests + cache work (screens and the agent chat) ───────────────

export async function fetchConformityDetail(
  id: string,
): Promise<ConformityDetail> {
  const { data } = await apiClientV2.get<ConformityDetail>(`${BASE}/${id}`)
  return data
}

/** Replace the extras list wholesale. The chip editor's add and remove are both
 *  a full PUT of the next list — see `ConformityRecipientEditor`. */
export async function updateConformityRecipientsRequest(
  id: string,
  emails: string[],
): Promise<ConformityRecipientsOut> {
  const { data } = await apiClientV2.patch<ConformityRecipientsOut>(
    `${BASE}/${id}/recipients`,
    { extraRecipientEmails: emails },
  )
  return data
}

/** Omit a half to leave it as stored; 409 LOCKED once sent or downloaded. */
export async function updateCoverNoteRequest(
  id: string,
  patch: { coverSubject?: string; coverBody?: string },
): Promise<ConformityDetail> {
  const { data } = await apiClientV2.patch<ConformityDetail>(
    `${BASE}/${id}/cover-note`,
    patch,
  )
  return data
}

/** signatureId=null clears it; no sent/downloaded lock (unlike cover-note);
 *  422 if the id doesn't resolve to a signature owned by the caller's own org. */
export async function updateConformitySignatureRequest(
  id: string,
  signatureId: string | null,
): Promise<ConformityDetail> {
  const { data } = await apiClientV2.patch<ConformityDetail>(
    `${BASE}/${id}/signature`,
    { signatureId },
  )
  return data
}

/** Draft only; 409 TEMPLATE_NOT_PUBLISHED if the named template has no
 *  published version. */
export async function updateConformityTemplateRequest(
  id: string,
  templateKey: string,
): Promise<ConformityDetail> {
  const { data } = await apiClientV2.patch<ConformityDetail>(
    `${BASE}/${id}/template`,
    { templateKey },
  )
  return data
}

/** A new batch of drafts changes the inbox listing and its counters. */
export function invalidateConformityList(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["mc", "conformity", "list"] })
  queryClient.invalidateQueries({ queryKey: ["mc", "conformity", "counts"] })
}

/** One declaration's editor — recipients, cover note, and the rendered preview
 *  the cover note appears in. An open editor does not refetch on its own, so a
 *  chat write that skipped this would read as having failed. */
export function invalidateConformityDeclaration(
  queryClient: QueryClient,
  id: string,
) {
  queryClient.invalidateQueries({
    queryKey: ["mc", "conformity", "detail", id],
  })
  queryClient.invalidateQueries({
    queryKey: ["mc", "conformity", "preview-html", id],
  })
  // Block-editor tree (2026-09-17 design): a left-panel action recomputes
  // detail/subjects server-side, and the merge step folds that into
  // document_blocks — refetching here is what surfaces it. Paragraph wording
  // the user already typed is untouched by that merge, so this refresh never
  // clobbers an in-progress edit.
  queryClient.invalidateQueries({
    queryKey: ["mc", "conformity", "document-blocks", id],
  })
}

// ─── Preview HTML (fetched, not iframe-src) ──────────────────────────────────

/**
 * Fetches the rendered HTML for a declaration via axios so the auth header
 * comes along. Callers pipe the returned string into `<iframe srcdoc={...} />`.
 * `<iframe src="/api/…">` would bypass axios and hit an unauthed request path
 * that Vite would fall back to `index.html` for — surfacing the SPA no-JS text
 * inside the preview pane.
 */
export function useConformityPreviewHtml(
  id: string | null,
  lang?: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["mc", "conformity", "preview-html", id, lang ?? null],
    queryFn: () =>
      apiClientV2
        .get<string>(`${BASE}/${id}/preview`, {
          params: lang ? { lang } : undefined,
          headers: { Accept: "text/html" },
          // apiClientV2 auto-transforms JSON responses to camelCase — bypass
          // that by treating this response as opaque text.
          transformResponse: (data) => data,
        })
        .then((r) => r.data),
    enabled: !!id && (options.enabled ?? true),
  })
}

/**
 * GET /{id}/pdf as a browser object URL. For an ISSUED v2 row the backend
 * returns the FROZEN bytes (pdf_file_key), so this is the artefact itself —
 * what the "Frozen PDF" pane must show instead of a live re-render (review
 * NB1). The URL is revoked when the query result is dropped.
 */
export function useConformityFrozenPdfUrl(id: string | null, enabled = true) {
  const query = useQuery({
    queryKey: ["mc", "conformity", "frozen-pdf", id],
    queryFn: async () => {
      const r = await apiClientV2.get(`${BASE}/${id}/pdf`, {
        responseType: "blob",
      })
      return URL.createObjectURL(r.data as Blob)
    },
    enabled: !!id && enabled,
    staleTime: Infinity, // frozen bytes never change
  })
  const url = query.data
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])
  return query
}

/** Fetches the (frozen or live) PDF as a browser object URL, through the
 *  authenticated client (a plain `<iframe src={api url}>` would bypass the
 *  auth header). The caller owns the URL and revokes it. */
export async function fetchConformityPdfUrl(
  id: string,
  lang?: string,
): Promise<string> {
  const r = await apiClientV2.get(`${BASE}/${id}/pdf`, {
    params: lang ? { lang } : undefined,
    responseType: "blob",
  })
  return URL.createObjectURL(r.data as Blob)
}

// ─── Download (single PDF or ZIP) ─────────────────────────────────────────────

/**
 * Download one or many declarations. The backend returns a single PDF when
 * exactly one id is passed, and a ZIP when there are multiple — Content-Type +
 * Content-Disposition tell the browser what to do. We trigger the save via a
 * hidden anchor so the response stream becomes a real file on disk (axios's
 * blob path swallows the suggested filename).
 *
 * Each downloaded row is marked DOWNLOADED on the server in the same request,
 * so callers should invalidate the list cache afterwards.
 */
export async function downloadConformityBundle(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const response = await apiClientV2.get(`${BASE}/download`, {
    params: { ids },
    // Match FastAPI's `?ids=a&ids=b` list convention — see `useConformityList`.
    paramsSerializer: { indexes: null },
    responseType: "blob",
  })
  const blob = response.data as Blob
  const fallback =
    ids.length === 1 ? `conformity-${ids[0]}.pdf` : "declarations.zip"
  const filename =
    parseContentDispositionFilename(response.headers["content-disposition"]) ??
    fallback
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function useDownloadConformity() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (ids: string[]) => downloadConformityBundle(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc", "conformity", "list"] })
    },
  })
  // The BE endpoint (mc_conformity.py:252) has no require_permission today,
  // but it mutates state (marks rows DOWNLOADED). Guard on VIEW at minimum so
  // pure viewers can't churn state via the client. Tighten if BE ever adds a
  // stricter gate.
  return useGuardedMutation(mutation, PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW)
}

// ─── Conformity v2 (spec §5.3 — one document per scope, all frameworks) ──────

export type ConformityV2Scope = "customer" | "products" | "general"
export type ConformityV2Class =
  | "favourable"
  | "conditional"
  | "indeterminate"
  | "adverse"
export type ConformityV2BlockedReason =
  | "gap"
  | "adverse"
  | "dependency"
  // The prerequisite was never evaluated — the org does not carry it as a
  // regulation. Distinct from "dependency" (evaluated, but blocked or adverse)
  // because the user's next action differs: adopt the regulation, not close a gap.
  | "dependency_missing"
  | "unconfigured"
export type ConformityV2StatementVariant =
  | "compliant"
  | "compliant_with_condition"
  | "scoped"

/** Scope for /v2/preview and /v2/generate. `customer` resolves subjects from
 *  the customer's mapped products; `products` takes explicit product ids;
 *  `general` (TUR-1573) makes no per-product claim: an optional customer plus
 *  a free-text product family description, no subject ids at all. */
export type ConformityV2Target = (
  | { scope: "customer"; customerId: string }
  | { scope: "products"; subjectIds: string[] }
  // productDescription is OPTIONAL and only NARROWS the letter: absent means the
  // declaration covers all of the issuer's products. The preview target omits it
  // entirely, because a general evaluation does not depend on it.
  | {
      scope: "general"
      customerId: string | null
      productDescription?: string | null
    }
) & {
  /** The regulations picked on the generate screen (TUR-1733), checked and
   *  closed server-side. Absent: the org set and the customer's own, as before. */
  regulationKeys?: string[]
}

/** One regulation the generate screen's picker offers (TUR-1733). */
export interface ConformityRegulationOption {
  key: string
  /** In Settings › Regulations: preselected. */
  inOrgSet: boolean
  /** One of the customer's own regulations: preselected for that customer. */
  inCustomerSet: boolean
  /** Can be declared: v2 copy and its list editions. */
  pickable: boolean
}

export interface ConformityV2Register {
  tag: string
  name: string
  publishedAt: string | null
  substanceCount: number | null
}

export interface ConformityV2Gap {
  articleId: string
  articleNumber: string | null
  articleName: string | null
  supplierId: string | null
  supplierName: string | null
  reason: "missing" | "stale" | "no_bom" | "no_supplier_data"
  subjectCount: number
  /** Supplier inquiry trail — "last inquiry 12 Jun 2026 · no reply". */
  lastInquiryAt: string | null
  inquiryStatus: string | null
}

/** SVHC hits grouped by (name, cas) — max concentration across components. */
export interface ConformityV2SvhcHit {
  name: string
  cas: string | null
  maxConcentrationPct: number | null
  components: string[]
  scipNumbers: string[]
}

export interface ConformityV2Exemption {
  code: string | null
  substance: string | null
  cas: string | null
  components: string[]
  expiryDate: string | null
  application: string | null
  maxConcentrationPct: number | null
}

/** Per product×component table row (kind svhc | exemption) — the editor's
 *  expanded detail. Product attribution the grouped hits cannot provide. */
export interface ConformityV2DetailRow {
  kind: "svhc" | "exemption"
  productId: string
  productNumber: string | null
  productName: string | null
  articleId: string
  articleNumber: string | null
  articleName: string | null
  supplierId: string | null
  supplierName: string | null
  substance: string | null
  cas: string | null
  maxConcentrationPct: number | null
  /** svhc rows only */
  supplierScip?: string | null
  ownScip?: string | null
  /** exemption rows only */
  code?: string | null
  application?: string | null
  expiryDate?: string | null
}

/** One contradicting article, named well enough by number and name alone
 *  (design section 6.3: attributing it back to a product needs an extra
 *  query the bounded backend methods exist to avoid). */
export interface ConformityV2ContradictionSample {
  articleId: string
  articleNumber: string | null
  articleName: string | null
}

/** General scope only (TUR-1573, design section 6): master data that
 *  contradicts this framework's knowledge-based statement. Counts are exact;
 *  `samples` is capped server-side. */
export interface ConformityV2Contradiction {
  svhcCount: number
  adverseCount: number
  samples: ConformityV2ContradictionSample[]
}

export interface ConformityV2Framework {
  frameworkKey: string
  label: string
  computedClass: ConformityV2Class
  statementVariant: ConformityV2StatementVariant | null
  blockedReason: ConformityV2BlockedReason | null
  selected: boolean
  registers: ConformityV2Register[]
  /** detail_missing | exemption_unresolved — editor flags, never blocks. */
  flags: string[]
  gaps: ConformityV2Gap[]
  exemptions: ConformityV2Exemption[]
  svhcHits: ConformityV2SvhcHit[]
  rows: ConformityV2DetailRow[]
  /** True when the backend capped `rows` (CONFORMITY_V2_ROWS_CAP). */
  rowsTruncated: boolean
  notApplicable: string[]
  /** Product ids missing their OWN SCIP number (Q16) — approve gate. */
  scipMissing: string[]
  overrideReason: string | null
  overrideBy: string | null
  overrideAt: string | null
  overridable: boolean
  overrideBlocker: ConformityV2OverrideBlocker | null
  /** No component of this framework carries supplier data. Does NOT block the
   *  override (decision 2026-09-17): the confirm dialog warns with it that the
   *  user is asserting conformity on their own responsibility. */
  allComponentsUncovered: boolean
  /** General scope only: null on every other scope, and on general when
   *  nothing contradicts this framework (design section 6). */
  contradictions: ConformityV2Contradiction | null
  /** General scope only: the printed sentence naming what this framework's
   *  clean statement does not cover (design section 6.5). */
  disclosureText: string | null
  /** General scope only: an audited "declare on your own responsibility"
   *  reason (design section 6.5); not printed. */
  ackReason: string | null
  ackBy: string | null
  ackAt: string | null
  /** True when an ackReason exists but the contradicting set changed since it
   *  was given (design section 6.5.1): the acknowledgement no longer counts
   *  and approve refuses again. Optional: lands on the backend in parallel
   *  with this task, so it may be absent on an older server. */
  ackOutdated?: boolean
  /** True when a disclosureText exists but the contradicting set changed
   *  since it was written (design section 6.5.1): a hint for the author,
   *  never a block. Optional, same reason as ackOutdated. */
  disclosureOutdated?: boolean
}

export type ConformityV2OverrideBlocker = "not_gap" | "reason_not_overridable"

export interface ConformityV2Subject {
  kind: string
  subjectId: string
  number: string
  name: string | null
  revision: string | null
  customerNumber: string | null
}

/** Wire values of the rollup's `empty_reason` (mc_conformity_rollup.py
 *  EMPTY_*). One place, so a typo at a comparison site cannot silently fall
 *  through to the "has frameworks" branch. */
export const CONFORMITY_EMPTY_REASON = {
  noCustomerProducts: "no_customer_products",
  noFrameworksConfigured: "no_frameworks_configured",
} as const

export interface ConformityV2Evaluation {
  scope: ConformityV2Scope
  subjectKey: string
  language: string
  freshnessDays: number
  subjects: ConformityV2Subject[]
  frameworks: ConformityV2Framework[]
  /** "no_customer_products" → the empty state with the customer-record link;
   *  "no_frameworks_configured" → the org subscribes to no regulation, so
   *  nothing can be declared (Generate disabled, server 409s as backstop). */
  emptyReason: string | null
}

export interface ConformityV2ApproveErrorProduct {
  productId: string
  productNumber: string
}

export type ConformityV2ApproveErrorCode =
  | "STALE_EVALUATION"
  | "SIGNATORY_MISSING"
  // Split out of SIGNATORY_MISSING: the fixes live in different places — attach a
  // signature in the editor vs set the issuing place in settings — and one code
  // made the refusal panel guess which to offer.
  | "PLACE_MISSING"
  | "NO_FRAMEWORK"
  | "SCIP_REFERENCE_MISSING"
  | "CONTRADICTING_DATA"
  // approve_v2 refuses on this too; it was missing here, so the panel fell through
  // to the stale default copy.
  | "DOCUMENT_TOO_LARGE"

/** One unresolved contradicting framework named by a CONTRADICTING_DATA
 *  refusal (general scope, TUR-1573, design section 6.5.1). */
export interface ConformityV2ApproveErrorFramework {
  frameworkKey: string
  svhcCount: number
  adverseCount: number
}

export interface ConformityV2ApproveError {
  code: ConformityV2ApproveErrorCode
  products?: ConformityV2ApproveErrorProduct[]
  frameworks?: ConformityV2ApproveErrorFramework[]
}

/**
 * Extracts the APPROVE_REFUSED error list from a failed approve call, or null
 * for any other failure. Error responses skip the camelCase interceptor, so
 * the raw snake_case wire keys are mapped here.
 */
export function approveRefusedErrors(
  error: unknown,
): ConformityV2ApproveError[] | null {
  const detail = (
    error as {
      response?: {
        data?: { detail?: { code?: string; errors?: unknown[] } }
      }
    }
  )?.response?.data?.detail
  if (detail?.code !== "APPROVE_REFUSED" || !Array.isArray(detail.errors)) {
    return null
  }
  return detail.errors.map((entry) => {
    const raw = entry as {
      code: ConformityV2ApproveErrorCode
      products?: { product_id: string; product_number: string }[]
      frameworks?: {
        framework_key: string
        svhc_count: number
        adverse_count: number
      }[]
    }
    return {
      code: raw.code,
      products: raw.products?.map((p) => ({
        productId: p.product_id,
        productNumber: p.product_number,
      })),
      frameworks: raw.frameworks?.map((f) => ({
        frameworkKey: f.framework_key,
        svhcCount: f.svhc_count,
        adverseCount: f.adverse_count,
      })),
    }
  })
}

/**
 * Reads a SCOPE_TOO_LARGE refusal (WP1) off a failed preview/generate, or null
 * for any other failure. Error responses skip the camelCase interceptor, so the
 * wire keys are read raw.
 */
function countLimitRefusal(
  error: unknown,
  code: string,
): { count: number; limit: number } | null {
  const detail = (
    error as {
      response?: {
        data?: { detail?: { code?: string; count?: number; limit?: number } }
      }
    }
  )?.response?.data?.detail
  if (detail?.code !== code) return null
  return { count: detail.count ?? 0, limit: detail.limit ?? 0 }
}

/** The scope holds more products than the org allows to evaluate at once. */
export function scopeTooLargeDetail(error: unknown) {
  return countLimitRefusal(error, "SCOPE_TOO_LARGE")
}

/** The exception annex would run past what a signed declaration can carry, so
 *  generate refused rather than truncate it. */
export function documentTooLargeDetail(error: unknown) {
  return countLimitRefusal(error, "DOCUMENT_TOO_LARGE")
}

/** generate_v2 refused because the org subscribes to no regulation. The
 *  button is disabled on the same condition, so this only fires when the
 *  preview was stale. */
export function noFrameworksRefused(error: unknown): boolean {
  return apiErrorDetail(error)?.code === "NO_FRAMEWORKS_CONFIGURED"
}

/**
 * Reads the `{code, ...}` 409/422 error body a v2 write endpoint refuses
 * with (NOT_OVERRIDABLE, FORBIDDEN, REASON_TOO_SHORT, ...). Error responses
 * skip the camelCase interceptor, so the raw wire keys are read directly.
 */
export function apiErrorDetail(
  error: unknown,
): { code?: string; blocker?: ConformityV2OverrideBlocker } | null {
  const detail = (
    error as {
      response?: { data?: { detail?: { code?: string; blocker?: string } } }
    }
  )?.response?.data?.detail
  if (!detail?.code) {
    return null
  }
  return {
    code: detail.code,
    blocker: detail.blocker as ConformityV2OverrideBlocker | undefined,
  }
}

/** apiClientV2 snake_cases on the wire; the backend wants both fields present. */
export function v2TargetBody(target: ConformityV2Target) {
  // Sent only when picked: absent keeps the server's own set (the assistant's
  // generate, an old client).
  const regulations = target.regulationKeys
    ? { regulationKeys: target.regulationKeys }
    : {}
  if (target.scope === "customer") {
    return {
      scope: target.scope,
      subjectIds: [],
      customerId: target.customerId,
      ...regulations,
    }
  }
  if (target.scope === "general") {
    return {
      scope: target.scope,
      subjectIds: [],
      customerId: target.customerId,
      // '' would be a family name the user never typed; null is the absence the
      // backend stores and the document renders as "all our products".
      productDescription: target.productDescription || null,
      ...regulations,
    }
  }
  return {
    scope: target.scope,
    subjectIds: target.subjectIds,
    customerId: null,
    ...regulations,
  }
}

export async function conformityRegulationOptionsRequest(
  customerId: string | null,
): Promise<ConformityRegulationOption[]> {
  const { data } = await apiClientV2.get<{
    regulations: ConformityRegulationOption[]
  }>(`${BASE}/v2/regulations`, {
    params: customerId ? { customer_id: customerId } : undefined,
  })
  return data.regulations
}

/** GET /v2/regulations — what the generate screen's picker offers, marked as in
 *  the org set, among the customer's own (with `customerId`) and declarable. */
export function useConformityRegulationOptions(customerId: string | null) {
  return useQuery({
    queryKey: ["mc", "conformity", "v2", "regulation-options", customerId],
    queryFn: () => conformityRegulationOptionsRequest(customerId),
  })
}

export async function previewConformityV2Request(
  target: ConformityV2Target,
): Promise<ConformityV2Evaluation> {
  const { data } = await apiClientV2.post<ConformityV2Evaluation>(
    `${BASE}/v2/preview`,
    v2TargetBody(target),
  )
  return data
}

export async function generateConformityV2Request(
  target: ConformityV2Target,
  onePerSubject = false,
  templateKey: string | null = null,
): Promise<{ declarationIds: string[] }> {
  const { data } = await apiClientV2.post<{ declarationIds: string[] }>(
    `${BASE}/v2/generate`,
    { ...v2TargetBody(target), onePerSubject, templateKey },
  )
  return data
}

export async function createSellThroughProductsRequest(
  articleIds: string[],
): Promise<{ productIds: string[] }> {
  const { data } = await apiClientV2.post<{ productIds: string[] }>(
    `${BASE}/v2/sell-through-products`,
    { articleIds },
  )
  return data
}

/** POST /v2/preview — evaluate the scope, nothing persisted. Query form so the
 *  result refreshes when the selection changes; sorted ids keep the cache key
 *  stable across re-ordered selections. */
export function useConformityV2Preview(target: ConformityV2Target | null) {
  const sorted =
    target?.scope === "products"
      ? { ...target, subjectIds: [...target.subjectIds].sort() }
      : target
  return useQuery({
    queryKey: ["mc", "conformity", "v2", "preview", sorted],
    queryFn: () => previewConformityV2Request(sorted!),
    enabled:
      !!sorted &&
      (sorted.scope === "customer"
        ? !!sorted.customerId
        : // A general evaluation needs nothing beyond the scope: the letter
          // covers all of the issuer's products unless a family narrows it, and
          // the description is not part of the evaluation at all.
          sorted.scope === "general" || sorted.subjectIds.length > 0),
  })
}

/** POST /v2/generate — persist ONE draft per scope (or one per product with
 *  `onePerSubject`). Idempotent per subject_key: an existing draft is
 *  re-evaluated and its id returned. */
export function useGenerateConformityV2() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (
      body: ConformityV2Target & {
        onePerSubject?: boolean
        /** Phase-3 template switcher; null = the org default. */
        templateKey?: string | null
      },
    ) =>
      generateConformityV2Request(
        body,
        body.onePerSubject ?? false,
        body.templateKey ?? null,
      ),
    onSuccess: () => invalidateConformityList(qc),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** POST /v2/sell-through-products — Q19: purchased articles sold unchanged
 *  become products with a one-article BOM. Idempotent. */
export function useCreateSellThroughProducts() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (articleIds: string[]) =>
      createSellThroughProductsRequest(articleIds),
    // The new products must show up in the mounted ProductPickerTable
    // (useProductList's ["mc", "products", "list", ...] query) without a
    // manual refresh — same invalidation as useSetProductScipNumber below.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc", "products"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

/** GET /{id}/frameworks — the persisted per-framework children of a v2 draft.
 *
 *  Polled on the same terms as the detail query: a background recompute
 *  REWRITES these children, so a panel reading only this query would keep
 *  showing the pre-recompute numbers after `evaluationState` had already gone
 *  back to "ready". `isComputing` is passed in by the caller, which already has
 *  the detail — this hook does not fetch it a second time. */
export function useConformityDeclarationFrameworks(
  declarationId: string | null,
  isComputing = false,
) {
  return useQuery({
    queryKey: ["mc", "conformity", "v2", "frameworks", declarationId],
    queryFn: () =>
      apiClientV2
        .get<ConformityV2Framework[]>(`${BASE}/${declarationId}/frameworks`)
        .then((r) => r.data),
    enabled: !!declarationId,
    refetchInterval: isComputing ? COMPUTING_POLL_MS : false,
  })
}

function invalidateV2Draft(qc: QueryClient, id: string) {
  qc.invalidateQueries({
    queryKey: ["mc", "conformity", "v2", "frameworks", id],
  })
  invalidateConformityDeclaration(qc, id)
  // Selection/override/re-evaluate change frameworkKeys + has_gap on the
  // inbox row too (review NF8).
  invalidateConformityList(qc)
}

/** DELETE /conformity/{id} — discard a DRAFT (409 NOT_DRAFT once approved:
 *  an issued declaration keeps its reference and frozen PDF). */
export function useDeleteConformityDeclaration() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) => apiClientV2.delete(`${BASE}/${id}`),
    onSuccess: () => invalidateConformityList(qc),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** PATCH /{id}/frameworks/{key} — the include checkbox. The backend mirrors
 *  the XVII↔PFAS dependency, so both rows are refetched via invalidation. */
export function useSetFrameworkSelection() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      frameworkKey,
      selected,
    }: {
      id: string
      frameworkKey: string
      selected: boolean
    }) =>
      apiClientV2
        .patch<ConformityV2Framework>(
          `${BASE}/${id}/frameworks/${encodeURIComponent(frameworkKey)}`,
          { selected },
        )
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** PATCH /{id}/product-list — print the full product table in Annex 1 instead of
 *  naming the scope by reference. Per declaration, draft only. */
export function useSetPrintProductList() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      printProductList,
    }: {
      id: string
      printProductList: boolean
    }) =>
      apiClientV2
        .patch(`${BASE}/${id}/product-list`, {
          printProductList,
        })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

// ─── Block editor (2026-09-17 design) ────────────────────────────────────────

/** GET /declaration.css — the exact stylesheet conformity_renderer_v2 inlines
 *  into the PDF/preview HTML, fetched once (cached indefinitely — it's a
 *  static asset, not per-declaration) so the block editor's system fragments
 *  and paragraph text render with the same rules the eventual PDF uses. Fetched
 *  via axios rather than a plain `<link>` for the same reason as the preview
 *  HTML: the route needs the auth header, which only axios attaches. */
export function useConformityDeclarationCss() {
  return useQuery({
    queryKey: ["mc", "conformity", "declaration-css"],
    queryFn: () =>
      apiClientV2
        .get<string>(`${BASE}/declaration.css`, {
          headers: { Accept: "text/css" },
          transformResponse: (data) => data,
        })
        .then((r) => r.data),
    staleTime: Infinity,
  })
}

// Discriminated inline/block node shapes — must match the backend's
// ConformityInlineNode / ConformityBlockNode exactly (turnus_ai/mc_schemas.py):
// a `paragraph` node is the only one a client may ever send content for, and
// only `text` (+ an optional bold mark) and `hardBreak` are legal inline nodes.
export interface ConformityInlineTextNode {
  type: "text"
  text: string
  marks?: Array<{ type: "bold" }>
}
export interface ConformityInlineHardBreakNode {
  type: "hardBreak"
}
export type ConformityInlineNode =
  | ConformityInlineTextNode
  | ConformityInlineHardBreakNode

export interface ConformityParagraphNode {
  type: "paragraph"
  attrs: { slot: string }
  content: ConformityInlineNode[]
}
export interface ConformitySystemFragmentNode {
  type: "systemFragment"
  attrs: { key: string; html: string }
}
export type ConformityBlockNode =
  | ConformityParagraphNode
  | ConformitySystemFragmentNode

export interface ConformityDocumentBlocksTree {
  type: "doc"
  content: ConformityBlockNode[]
}

export interface ConformityBlockDocument {
  documentBlocks: ConformityDocumentBlocksTree
  /** The rendered document's own (per-language) heading — the toolbar shows
   *  this instead of fetching the full preview HTML just to read `<title>`. */
  title: string
  /** design §4: the page margins this declaration's header/footer strips
   *  imply (`declarationTemplateApi.ts`'s `PageGeometry` — same shape as the
   *  template draft's, computed the same way server-side). */
  pageGeometry: PageGeometry
  /** design §8: what the PDF prints INSIDE those margins, as the inner
   *  fragment of the Gotenberg header/footer document — the strips are never
   *  part of `documentBlocks`, so without these the editor's sheet margins
   *  would be blank although every declaration prints at least the legacy
   *  footer. `null` = nothing prints there. Trusted server markup, rendered
   *  inert (`contenteditable=false`, `aria-hidden`) by `PaperScope`. */
  pageHeaderHtml: string | null
  pageFooterHtml: string | null
}

/** GET /{id}/document-blocks — the block editor's document tree. Purely
 *  derived from current data plus any paragraphs the user has edited — the
 *  request never writes anything. Refetched whenever any left-panel action
 *  invalidates the declaration (`invalidateConformityDeclaration`): edited
 *  wording is read back from storage on every derive, so this never clobbers
 *  an in-progress edit, only refreshes system content (framework tables,
 *  annex, signature). */
export function useConformityDocumentBlocks(
  id: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["mc", "conformity", "document-blocks", id],
    queryFn: () =>
      apiClientV2
        .get<ConformityBlockDocument>(`${BASE}/${id}/document-blocks`)
        .then((r) => r.data),
    enabled: !!id && (options.enabled ?? true),
  })
}

/** PATCH /{id}/document-blocks — autosave one edited paragraph. Debounced by
 *  the caller (~600-800ms idle); this hook itself fires immediately once
 *  called. Deliberately does NOT invalidate `preview-html` — that would
 *  re-trigger the exact full-document round-trip this feature exists to
 *  avoid. The returned (freshly re-derived) tree replaces the cache directly. */
export function useSaveConformityDocumentBlockParagraph() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      slot,
      content,
    }: {
      id: string
      slot: string
      content: ConformityInlineNode[]
    }) =>
      apiClientV2
        .patch<ConformityBlockDocument>(`${BASE}/${id}/document-blocks`, {
          slot,
          content,
        })
        .then((r) => r.data),
    onSuccess: (result, { id }) => {
      qc.setQueryData(["mc", "conformity", "document-blocks", id], result)
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** POST /{id}/frameworks/{key}/override (Q2): declare despite missing, stale
 *  or no-supplier-data verdicts. Reason ≥ 10 chars; audited; produces Annex 3. */
export function useOverrideFramework() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      frameworkKey,
      reason,
    }: {
      id: string
      frameworkKey: string
      reason: string
    }) =>
      apiClientV2
        .post<ConformityV2Framework>(
          `${BASE}/${id}/frameworks/${encodeURIComponent(frameworkKey)}/override`,
          { reason },
        )
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_OVERRIDE,
  )
}

/** DELETE /{id}/frameworks/{key}/override — back to the blocked state. */
export function useClearFrameworkOverride() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ id, frameworkKey }: { id: string; frameworkKey: string }) =>
      apiClientV2
        .delete<ConformityV2Framework>(
          `${BASE}/${id}/frameworks/${encodeURIComponent(frameworkKey)}/override`,
        )
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_OVERRIDE,
  )
}

/** POST /{id}/v2/scip-override: issue REACH_SVHC without the issuer's own SCIP
 *  number for these products. Reason ≥ 10 chars; audited. Approve still
 *  refuses any other product missing its number. */
export function useScipOverride() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      productIds,
      reason,
    }: {
      id: string
      productIds: string[]
      reason: string
    }) =>
      apiClientV2
        .post<ConformityV2Framework>(`${BASE}/${id}/v2/scip-override`, {
          productIds,
          reason,
        })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_OVERRIDE,
  )
}

/** PATCH /{id}/product-description (general scope only, TUR-1573). Draft
 *  only: editing recomputes `subject_key`, so the caller must be ready for a
 *  409 DESCRIPTION_TAKEN when the new wording collides with another open
 *  draft (design section 4.3). */
export function useSetProductDescription() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      productDescription,
    }: {
      id: string
      // null clears the family and widens the letter back to all products.
      productDescription: string | null
    }) =>
      apiClientV2
        .patch<ConformityDetail>(`${BASE}/${id}/product-description`, {
          productDescription,
        })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** PATCH /{id}/frameworks/{key}/disclosure (general scope only, TUR-1573):
 *  the printed sentence naming what a framework's clean statement does not
 *  cover. `text: null` clears it. No special permission: disclosing more is
 *  never the risky direction (design section 6.5). */
export function useSetFrameworkDisclosure() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      frameworkKey,
      text,
    }: {
      id: string
      frameworkKey: string
      text: string | null
    }) =>
      apiClientV2
        .patch<ConformityV2Framework>(
          `${BASE}/${id}/frameworks/${encodeURIComponent(frameworkKey)}/disclosure`,
          { text },
        )
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** POST /{id}/general-acknowledge (general scope only, TUR-1573): declare
 *  despite the named frameworks' contradicting master data. Reason >= 10
 *  chars; audited (`conformity_general_acknowledged`); not printed (design
 *  section 6.5/6.6). */
export function useAcknowledgeGeneral() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      id,
      frameworkKeys,
      reason,
    }: {
      id: string
      frameworkKeys: string[]
      reason: string
    }) =>
      apiClientV2
        .post<ConformityV2Framework[]>(`${BASE}/${id}/general-acknowledge`, {
          frameworkKeys,
          reason,
        })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_OVERRIDE,
  )
}

/** PATCH /master-data/products/{id}/scip-number — Q20: record the issuer's
 *  OWN SCIP notification number inline from the editor; the caller follows up
 *  with a re-evaluate so the gate and the table refresh. */
export function useSetProductScipNumber() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      productId,
      scipNumber,
    }: {
      productId: string
      scipNumber: string
    }) =>
      apiClientV2
        .patch(
          `/material-compliance/master-data/products/${productId}/scip-number`,
          { scipNumber },
        )
        .then((r) => r.data),
    onSuccess: (_data, { productId }) => {
      qc.invalidateQueries({ queryKey: ["mc", "products", productId] })
      qc.invalidateQueries({ queryKey: ["mc", "products"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}

interface ConformityReEvaluateOut {
  id: string
  /** "queued": the recompute is on the facts queue, poll `evaluationState`.
   *  "done": the org does not read facts, so the refresh already happened
   *  inline and one refetch is enough. Widened for the same deploy-skew reason
   *  as `evaluationState`; anything that is not "queued" is treated as done. */
  state?: "queued" | "done" | (string & {})
  pendingProductCount?: number
  /** Inline path only: the rollup's named empty reason, so the editor does
   *  not toast "refreshed" over an empty framework list. */
  emptyReason?: string | null
}

/** POST /{id}/re-evaluate — 202. Refreshes the draft against current master
 *  data; deselections survive, overrides drop.
 *
 *  Always 202, on both paths, so this hook never has to know whether the org is
 *  on the facts path — `state` says whether to poll or to refetch once. */
export function useReEvaluateConformity() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2
        .post<ConformityReEvaluateOut>(`${BASE}/${id}/re-evaluate`)
        .then((r) => r.data),
    onSuccess: (_data, id) => invalidateV2Draft(qc, id),
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  )
}

/** POST /{id}/approve-v2 — refuses with 409 APPROVE_REFUSED carrying EVERY
 *  failed gate at once (see `approveRefusedErrors`); on success allocates the
 *  MC-CDE reference and freezes the PDF. */
export function useApproveConformityV2() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2
        .post<ConformityApproveOut>(`${BASE}/${id}/approve-v2`)
        .then((r) => r.data),
    onSuccess: (_data, id) => {
      invalidateV2Draft(qc, id)
      invalidateConformityList(qc)
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}
